// Detalle de un envío de "Envíos > Lista legacy" (legacy.envios). Ficha
// operativa de solo lectura — mismo patrón de overlay que los otros modales
// de detalle del panel admin (ver DriverAdminDetailModal.tsx). Sin edición,
// sin financials. El mapa reutiliza useLeaflet (mismo hook que Choferes en
// vivo / resolución de geolocalización) — sin dependencia nueva, solo
// lectura, un marcador fijo, sin click-to-place ni geocodificación.
import React, { useEffect, useRef, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import { authFetch } from "../../lib/authFetch";
import { useLeaflet } from "../../hooks/useLeaflet";
import { useAuth } from "../../context/AuthContext";
import OverrideEditModal, { DeliveryDetail, ReportTarget } from "../liquidaciones/OverrideEditModal";

type EnvioLegacyDetail = {
  lightdata_id: number | string;
  numero_tracking: string | null;
  id_venta_ml: string | null;
  origen: string | null;
  metodo_envio: string | null;
  bultos: string | null;
  estado: string | null;
  fecha_estado: string | null;
  fecha_estado_ar: string | null;
  estado_ml: string | null;
  subestado_ml: string | null;
  nombre_fantasia: string | null;
  cadete: string | null;
  nombre_destinatario: string | null;
  direccion: string | null;
  cp: string | null;
  localidad: string | null;
  ciudad: string | null;
  tel_destinatario: string | null;
  email_destinatario: string | null;
  latitud: string | null;
  longitud: string | null;
  fecha_venta: string | null;
  fecha_colecta: string | null;
  fecha_envio: string | null;
  zonas_costos: string | null;
  costo_envio: string | null;
  observaciones: string | null;
};

type Props = {
  open: boolean;
  lightdataId: number | string | null;
  token: string | null;
  onClose: () => void;
};

// zIndex 130 (no 120): este modal se reutiliza también anidado dentro de
// ClienteDetailModal/DriverDetailModal (zIndex 120, ver "LYNX — FASE 3:
// Modal reutilizable") -- debe quedar siempre por encima de ese padre.
// 130 queda, a propósito, por debajo de OverrideEditModal (140), que este
// mismo componente abre internamente para "Crear Reporte Operativo" --
// ese caso ya existente (Lista Legacy) debe seguir funcionando igual.
const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 130,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
};
const backdropStyle: React.CSSProperties = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)" };
const shellStyle: React.CSSProperties = { position: "relative", width: "100%", maxWidth: "1040px", maxHeight: "90vh", overflow: "hidden" };
const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "18px 20px",
  borderBottom: "1px solid var(--border)",
};
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" };
const bodyStyle: React.CSSProperties = { padding: "20px", maxHeight: "80vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "18px" };
const columnsRowStyle: React.CSSProperties = { display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "flex-start" };
const leftColStyle: React.CSSProperties = { flex: "3 1 460px", display: "flex", flexDirection: "column", gap: "18px", minWidth: 0 };
const rightColStyle: React.CSSProperties = { flex: "1 1 280px", display: "flex", flexDirection: "column", gap: "12px", minWidth: 0 };
const sectionTitleStyle: React.CSSProperties = {
  margin: "0 0 10px 0",
  fontSize: "13px",
  fontWeight: 700,
  color: "var(--text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
const gridStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px" };
const fieldStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "4px" };
const labelStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const valueStyle: React.CSSProperties = { fontSize: "14px", color: "var(--text-primary)", wordBreak: "break-word" };
const stateStyle: React.CSSProperties = { padding: "24px 0", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };
const mapContainerStyle: React.CSSProperties = {
  height: "200px",
  borderRadius: "12px",
  overflow: "hidden",
  border: "1px solid var(--border)",
  background: "var(--surface-2, #0c0e12)",
};
const mapPlaceholderStyle: React.CSSProperties = { ...stateStyle, padding: 0, height: "200px", display: "flex", alignItems: "center", justifyContent: "center" };
const bottomBarStyle: React.CSSProperties = { borderTop: "1px solid var(--border)", paddingTop: "16px" };
const tabBarStyle: React.CSSProperties = { display: "flex", gap: "6px", borderBottom: "1px solid var(--border)", paddingBottom: "10px" };
const tabBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: "7px 14px",
  borderRadius: "10px",
  border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
  background: active ? "var(--primary-soft)" : "transparent",
  color: active ? "var(--primary)" : "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  cursor: "pointer",
});
const historyTableWrapStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "14px", overflowX: "auto" };
const historyTableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const historyThStyle: React.CSSProperties = { textAlign: "left", padding: "10px", color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "rgba(255,255,255,0.02)" };
const historyTdStyle: React.CSSProperties = { padding: "8px 10px", color: "var(--text-primary)", fontSize: "13px", borderBottom: "1px solid var(--border)" };
const firstKnownStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)", fontStyle: "italic" };
const sourceBadgeStyle = (source: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "2px 9px",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: 700,
  border: `1px solid ${source === "poller" ? "var(--primary, #4f8cff)" : "#c99a1f"}`,
  color: source === "poller" ? "var(--primary, #4f8cff)" : "#c99a1f",
});

type HistorialRow = {
  estado: string | null;
  estado_ml: string | null;
  fecha_estado: string | null;
  observed_at: string;
  source: string;
};

// Hora de pared Argentina, vía Intl con timeZone explícito -- mismo
// criterio que ya usa el resto de la app (ver DriverDetailModal.tsx/
// argentinaHour), nunca -3hs fijo ni dependiente del navegador.
function formatArTimestamp(raw: string | null): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString("es-AR", { timeZone: "America/Argentina/Buenos_Aires", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={fieldStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value ?? "—"}</span>
    </div>
  );
}

// Mapa chico read-only: centra en [lat,lng], un solo marcador fijo, sin
// controles de edición. Se crea una vez por apertura del modal (el modal
// entero desmonta al cerrar, así que no hereda el problema de ref-obsoleto
// que tuvo GeoResolutionPanel — acá alcanza con crear y, al desmontar,
// remove() para no dejar listeners de Leaflet colgados).
function EnvioMap({ lat, lng }: { lat: number; lng: number }) {
  const leaflet = useLeaflet();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  useEffect(() => {
    if (!leaflet || !containerRef.current || mapRef.current) return;
    const L = (window as any).L;
    const map = L.map(containerRef.current, {
      zoomControl: false,
      dragging: true,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      attributionControl: false,
    }).setView([lat, lng], 15);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18 }).addTo(map);
    const icon = L.divIcon({
      className: "",
      html: `<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;background:var(--primary,#4f8cff);border:2px solid #1a1a1a;transform:rotate(-45deg);box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 18],
    });
    L.marker([lat, lng], { icon, interactive: false }).addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [leaflet, lat, lng]);

  return (
    <div style={mapContainerStyle}>
      {!leaflet ? <div style={mapPlaceholderStyle}>Cargando mapa…</div> : <div ref={containerRef} style={{ width: "100%", height: "100%" }} />}
    </div>
  );
}

// Solapa HISTORIAL (ver "LYNX — FASE 2/3: Delivery Detail + History"):
// consulta GET /v1/envios-legacy/:id/history, que devuelve TODAS las
// fuentes reales existentes (poller + historical_reconciliation + lo que
// exista a futuro) sin aplicar el filtro source='poller' que usa Elite —
// esto es investigación/auditoría humana, nunca la vista que consume el
// motor. Orden cronológico ascendente, tal como lo manda el backend.
// Deliberadamente NO afirma "historial completo" ni "incompleto": la
// tabla es autoritativa solo desde su propio deploy (ver server/
// migration), un historial corto no implica que el envío no tuvo más
// transiciones antes, solo que no se registraron después del deploy.
function EnvioHistorialPanel({ loading, error, rows }: { loading: boolean; error: string | null; rows: HistorialRow[] | null }) {
  if (loading) return <div style={stateStyle}>Cargando historial…</div>;
  if (error) return <div style={{ ...stateStyle, color: "var(--danger, #d1242f)" }}>{error}</div>;
  if (!rows || rows.length === 0) {
    return <div style={stateStyle}>Sin registros de historial para este envío.</div>;
  }

  const primero = rows[0];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={firstKnownStyle}>
        Primer registro conocido: {formatArTimestamp(primero.fecha_estado || primero.observed_at)}
        {primero.estado ? ` — ${primero.estado}` : ""}
      </div>
      <div style={historyTableWrapStyle}>
        <table style={historyTableStyle}>
          <thead>
            <tr>
              <th style={historyThStyle}>Fecha/hora</th>
              <th style={historyThStyle}>Estado</th>
              <th style={historyThStyle}>Estado ML</th>
              <th style={historyThStyle}>Fuente</th>
              <th style={historyThStyle}>Observado (Lynx)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${row.observed_at}-${i}`}>
                <td style={historyTdStyle}>{formatArTimestamp(row.fecha_estado)}</td>
                <td style={historyTdStyle}>{row.estado || "—"}</td>
                <td style={historyTdStyle}>{row.estado_ml || "—"}</td>
                <td style={historyTdStyle}>
                  <span style={sourceBadgeStyle(row.source)}>{row.source === "poller" ? "Poller" : "Reconciliación histórica"}</span>
                </td>
                <td style={historyTdStyle}>{formatArTimestamp(row.observed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Perfiles que pueden crear un Reporte Operativo (mismo REPORT_VIEWERS que
// server/legacy/operationalReports/operationalReports.routes.js) — Picker y
// un admin sin perfil asignado no ven esta acción.
const REPORT_PROFILES = ["COORDINADOR", "LIQUIDACIONES", "ADMIN_TOTAL"];

type EntityLookup = {
  lightdataId: number | string;
  store: { id: number; name: string | null } | null;
  driver: { id: number; name: string | null } | null;
};

export default function EnvioLegacyDetailModal({ open, lightdataId, token, onClose }: Props) {
  const { adminProfile } = useAuth();
  const canCreateReport = adminProfile !== null && REPORT_PROFILES.includes(adminProfile);

  const [detail, setDetail] = useState<EnvioLegacyDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tabs GENERAL | HISTORIAL (ver "LYNX — FASE 3: Modal reutilizable").
  // HISTORIAL se resetea a GENERAL y descarta el historial cargado cada
  // vez que se abre un envío distinto -- nunca mostrar el historial del
  // envío anterior mientras carga el nuevo.
  const [tab, setTab] = useState<"GENERAL" | "HISTORIAL">("GENERAL");
  const [historial, setHistorial] = useState<HistorialRow[] | null>(null);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [historialError, setHistorialError] = useState<string | null>(null);

  // Reportar un envío desde acá (Sprint Operativo — Parte 3: "el reporte
  // debe generarse desde donde realmente ocurre el problema"). Reutiliza
  // GET /v1/reports/lookup/:tracking (el mismo que usa la búsqueda de
  // Reportes Operativos) para resolver Store/Driver — nunca una segunda
  // versión de ese matching.
  const [entityLookup, setEntityLookup] = useState<EntityLookup | null>(null);
  const [reportTarget, setReportTarget] = useState<{ target: ReportTarget; label: string } | null>(null);

  useEffect(() => {
    if (!open || !lightdataId || !token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    setEntityLookup(null);

    authFetch(`/v1/envios-legacy/${lightdataId}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar el detalle del envío");
        if (!cancelled) setDetail(payload.envio ?? null);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, lightdataId, token]);

  // Resetea la solapa e historial cargado cada vez que se abre un envío
  // distinto (o se cierra el modal) -- nunca mostrar el historial de un
  // lightdataId anterior.
  useEffect(() => {
    setTab("GENERAL");
    setHistorial(null);
    setHistorialError(null);
  }, [open, lightdataId]);

  // Lazy load (ver "LYNX — FASE 3"): el historial SOLO se pide la primera
  // vez que se abre la solapa HISTORIAL para este envío -- nunca al abrir
  // el modal en General, nunca de nuevo si ya se cargó para este mismo
  // lightdataId (historial !== null corta el efecto).
  useEffect(() => {
    if (!open || tab !== "HISTORIAL" || !lightdataId || !token || historial !== null) return;
    let cancelled = false;
    setHistorialLoading(true);
    setHistorialError(null);

    authFetch(`/v1/envios-legacy/${lightdataId}/history`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar el historial del envío");
        if (!cancelled) setHistorial(payload.historial ?? []);
      })
      .catch((e: any) => {
        if (!cancelled) setHistorialError(e.message);
      })
      .finally(() => {
        if (!cancelled) setHistorialLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, tab, lightdataId, token, historial]);

  useEffect(() => {
    if (!open || !detail?.numero_tracking || !token || !canCreateReport) return;
    let cancelled = false;
    authFetch(`/v1/reports/lookup/${encodeURIComponent(detail.numero_tracking)}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && payload?.ok !== false) {
          setEntityLookup({ lightdataId: payload.envio.lightdataId, store: payload.envio.store, driver: payload.envio.driver });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, detail?.numero_tracking, token, canCreateReport]);

  if (!open) return null;

  const lat = detail?.latitud ? Number(detail.latitud) : null;
  const lng = detail?.longitud ? Number(detail.longitud) : null;
  const hasCoords = lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);

  return (
    <div style={overlayStyle}>
      <div style={backdropStyle} onClick={onClose} />
      <div style={shellStyle}>
        <Card padding="0" style={{ display: "flex", flexDirection: "column" }}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>Envío {detail?.numero_tracking || lightdataId || ""}</h2>
            <Button variant="secondary" onClick={onClose}>
              Cerrar
            </Button>
          </div>

          <div style={bodyStyle}>
            {loading ? (
              <div style={stateStyle}>Cargando…</div>
            ) : error ? (
              <div style={{ ...stateStyle, color: "var(--danger, #d1242f)" }}>{error}</div>
            ) : !detail ? (
              <div style={stateStyle}>No se encontró el envío.</div>
            ) : (
              <>
                <div style={tabBarStyle}>
                  <button type="button" style={tabBtnStyle(tab === "GENERAL")} onClick={() => setTab("GENERAL")}>
                    General
                  </button>
                  <button type="button" style={tabBtnStyle(tab === "HISTORIAL")} onClick={() => setTab("HISTORIAL")}>
                    Historial
                  </button>
                </div>

                {tab === "HISTORIAL" ? (
                  <EnvioHistorialPanel loading={historialLoading} error={historialError} rows={historial} />
                ) : (
                <>
                <div style={columnsRowStyle}>
                  <div style={leftColStyle}>
                    <section>
                      <h3 style={sectionTitleStyle}>General</h3>
                      <div style={gridStyle}>
                        <Field label="Tracking" value={detail.numero_tracking} />
                        <Field label="LightData ID" value={detail.lightdata_id} />
                        <Field label="ID venta ML" value={detail.id_venta_ml} />
                        <Field label="Store / nombre fantasía" value={detail.nombre_fantasia} />
                        <Field label="Origen" value={detail.origen} />
                        <Field label="Método de envío" value={detail.metodo_envio} />
                        <Field label="Bultos" value={detail.bultos} />
                        <Field label="Fecha venta" value={detail.fecha_venta} />
                        <Field label="Fecha colecta" value={detail.fecha_colecta} />
                        <Field label="Fecha envío" value={detail.fecha_envio} />
                      </div>
                    </section>

                    <section>
                      <h3 style={sectionTitleStyle}>Estado</h3>
                      <div style={gridStyle}>
                        <Field label="Estado operativo" value={detail.estado} />
                        <Field label="Fecha estado (Argentina)" value={detail.fecha_estado_ar} />
                        <Field label="Estado ML" value={detail.estado_ml} />
                        <Field label="Subestado ML" value={detail.subestado_ml} />
                        <Field label="Chofer" value={detail.cadete} />
                      </div>
                    </section>

                    <section>
                      <h3 style={sectionTitleStyle}>Operación</h3>
                      <div style={gridStyle}>
                        <Field label="Zona costo" value={detail.zonas_costos} />
                        <Field label="Costo envío" value={detail.costo_envio} />
                      </div>
                    </section>
                  </div>

                  <div style={rightColStyle}>
                    {hasCoords ? (
                      <EnvioMap lat={lat as number} lng={lng as number} />
                    ) : (
                      <div style={mapContainerStyle}>
                        <div style={mapPlaceholderStyle}>Sin coordenadas</div>
                      </div>
                    )}

                    <section>
                      <h3 style={sectionTitleStyle}>Destino</h3>
                      <div style={{ ...gridStyle, gridTemplateColumns: "1fr" }}>
                        <Field
                          label="Destinatario"
                          value={detail.nombre_destinatario || "Sin informar"}
                        />
                        <Field label="Dirección" value={detail.direccion} />
                        <Field label="CP" value={detail.cp} />
                        <Field label="Localidad" value={detail.localidad} />
                        <Field label="Ciudad" value={detail.ciudad} />
                        <Field label="Teléfono" value={detail.tel_destinatario} />
                        <Field label="Email" value={detail.email_destinatario} />
                        <Field label="Latitud" value={detail.latitud} />
                        <Field label="Longitud" value={detail.longitud} />
                      </div>
                      {hasCoords ? (
                        <div style={{ marginTop: "8px" }}>
                          <a
                            href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: "13px", color: "var(--primary, #4f8cff)" }}
                          >
                            Abrir en mapa ↗
                          </a>
                        </div>
                      ) : null}
                    </section>
                  </div>
                </div>

                <div style={bottomBarStyle}>
                  <h3 style={sectionTitleStyle}>Observaciones</h3>
                  <span style={valueStyle}>{detail.observaciones || "—"}</span>
                </div>

                {canCreateReport && (
                  <div style={bottomBarStyle}>
                    <h3 style={sectionTitleStyle}>Acciones</h3>
                    {!entityLookup ? (
                      <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Resolviendo Store/Driver…</span>
                    ) : !entityLookup.store && !entityLookup.driver ? (
                      <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                        Este envío no está vinculado a ningún Store ni Driver todavía — no se puede reportar.
                      </span>
                    ) : (
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {entityLookup.store && (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              setReportTarget({
                                target: { lightdataId: entityLookup.lightdataId, storeId: entityLookup.store!.id },
                                label: `Store: ${entityLookup.store!.name || "—"}`,
                              })
                            }
                          >
                            📝 Crear Reporte Operativo (Store: {entityLookup.store.name || "—"})
                          </Button>
                        )}
                        {entityLookup.driver && (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              setReportTarget({
                                target: { lightdataId: entityLookup.lightdataId, driverId: entityLookup.driver!.id },
                                label: `Chofer: ${entityLookup.driver!.name || "—"}`,
                              })
                            }
                          >
                            📝 Crear Reporte Operativo (Chofer: {entityLookup.driver.name || "—"})
                          </Button>
                        )}
                        {entityLookup.store && entityLookup.driver && (
                          // "Ambos" (ver informe "LYNX — Fase 1.2.9"): mismo
                          // patrón ya implementado en ReportesOperativos.tsx
                          // (CoordinadorSearch) — un único reporte con
                          // store_id Y driver_id a la vez, el backend ya lo
                          // soporta de punta a punta. Acá faltaba la opción.
                          <Button
                            onClick={() =>
                              setReportTarget({
                                target: {
                                  lightdataId: entityLookup.lightdataId,
                                  storeId: entityLookup.store!.id,
                                  driverId: entityLookup.driver!.id,
                                },
                                label: `Store: ${entityLookup.store!.name || "—"} + Chofer: ${entityLookup.driver!.name || "—"}`,
                              })
                            }
                          >
                            📝 Crear Reporte Operativo (Ambos)
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                </>
                )}
              </>
            )}
          </div>
        </Card>
      </div>

      <OverrideEditModal
        open={reportTarget !== null}
        mode="report"
        entityLabel={reportTarget?.label ?? null}
        token={token}
        delivery={
          reportTarget
            ? ({
                lightdata_id: reportTarget.target.lightdataId,
                tracking: detail?.numero_tracking ?? null,
                fecha_colecta: null,
                destinatario: detail?.nombre_destinatario ?? null,
                direccion: detail?.direccion ?? null,
                original_cp: null,
                cp: detail?.cp ?? null,
                has_postal_code_override: false,
                tier: null,
                tariff_name: null,
                estado: detail?.estado ?? null,
                is_turbo: false,
                turbo_source: null,
                turbo_price: null,
                is_double: false,
                calculated_price: null,
                manual_price: null,
                final_price: null,
                pricing_status: "PENDING",
                pricing_reason: null,
                has_override: false,
                override_observation: null,
              } as DeliveryDetail)
            : null
        }
        reportTarget={reportTarget?.target}
        onClose={() => setReportTarget(null)}
        onSaved={() => setReportTarget(null)}
      />
    </div>
  );
}
